import React, { useState, useEffect, useCallback } from 'react';
import { Clock, CheckCircle, AlertTriangle, AlertOctagon, Timer } from 'lucide-react';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  expected: { label: 'Expected', color: 'bg-emerald-100 text-emerald-700 border-emerald-200', dot: 'bg-emerald-500', icon: Clock },
  urgent: { label: 'Urgent', color: 'bg-amber-100 text-amber-700 border-amber-200', dot: 'bg-amber-500', icon: AlertTriangle },
  critical: { label: 'Critical', color: 'bg-red-100 text-red-700 border-red-200', dot: 'bg-red-500', icon: AlertOctagon },
  completed: { label: 'Completed', color: 'bg-teal-100 text-teal-700 border-teal-200', dot: 'bg-teal-500', icon: CheckCircle },
};

const ChecklistSLATracker = ({ leadId, token }) => {
  const [sla, setSla] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchSLA = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/api/leads/${leadId}/checklist-sla`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        setSla(await res.json());
      }
    } catch { /* ignore */ }
    setLoading(false);
  }, [leadId, token]);

  useEffect(() => {
    fetchSLA();
    const interval = setInterval(fetchSLA, 60000);
    return () => clearInterval(interval);
  }, [fetchSLA]);

  if (loading || !sla) return null;

  const config = STATUS_CONFIG[sla.status] || STATUS_CONFIG.expected;
  const Icon = config.icon;
  const progressPct = sla.total > 0 ? Math.round((sla.completed / sla.total) * 100) : 0;

  return (
    <div className="space-y-3" data-testid="checklist-sla-tracker">
      {/* Status Badge + Timer Row */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border ${config.color}`} data-testid="sla-status-badge">
            <Icon size={13} />
            {config.label}
          </span>
          {sla.status !== 'completed' && (
            <span className="inline-flex items-center gap-1 text-xs text-gray-500" data-testid="sla-timer">
              <Timer size={12} />
              {sla.timer_display}
            </span>
          )}
        </div>
        <span className="text-xs font-medium text-gray-600" data-testid="sla-progress-text">
          {sla.completed}/{sla.total} completed
        </span>
      </div>

      {/* Progress Bar */}
      <div className="w-full bg-gray-200 rounded-full h-2" data-testid="sla-progress-bar">
        <div
          className={`h-2 rounded-full transition-all duration-500 ${
            sla.status === 'completed' ? 'bg-teal-500' :
            sla.status === 'critical' ? 'bg-red-500' :
            sla.status === 'urgent' ? 'bg-amber-500' : 'bg-emerald-500'
          }`}
          style={{ width: `${progressPct}%` }}
        />
      </div>

      {/* Pending items summary (only when not completed) */}
      {sla.status !== 'completed' && sla.pending_items.length > 0 && (
        <p className="text-xs text-gray-400">
          Pending: {sla.pending_items.slice(0, 4).join(', ')}
          {sla.pending_items.length > 4 && ` +${sla.pending_items.length - 4} more`}
        </p>
      )}
    </div>
  );
};

export default ChecklistSLATracker;
